import { useQueryClient } from '@tanstack/react-query';

import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { useOpenChat } from '@/features/chat/use-open-chat';

import {
  notesStoreQueryKey,
  useDeleteNoteMutation,
  useSaveNoteMutation,
} from './assistant-notes.query';

import type { NoteItem } from './assistant-notes.types';

/** Asks the user to confirm deleting one note */
const confirmNoteDeletion = async (): Promise<boolean> =>
  Boolean(
    await ModalsManager.showResolvable(ConfirmDialog, {
      title: 'Delete this note?',
      description: 'Littlebird forgets this note. This cannot be undone.',
      confirmText: 'Delete note',
      cancelText: 'Cancel',
    })
  );

/**
 * The edit, delete, and source-chat actions of one row of the notes list. While the edit form is
 * open, `item` is the note as the form opened it, and `stopEditing` closes the form.
 */
export function useNoteItemActions(item: NoteItem, stopEditing: () => void) {
  const queryClient = useQueryClient();
  const saveNote = useSaveNoteMutation();
  const deleteNote = useDeleteNoteMutation();
  const { openChat, isOpening } = useOpenChat();

  return {
    cancelEditing: () => {
      stopEditing();
      // The note may have changed elsewhere while the form was open, and the list does not reload
      // on focus, so the row shows the note as the server has it now
      void queryClient.invalidateQueries({ queryKey: notesStoreQueryKey() });
    },
    isSaving: saveNote.isPending,
    // Every save sends the version the form opened with, so a note that changed elsewhere
    // refuses each save until the user discards and opens the form again
    save: (text: string) =>
      saveNote.mutate(
        { number: item.number, text, version: item.version },
        { onSuccess: stopEditing }
      ),
    isDeleting: deleteNote.isPending,
    remove: async () => {
      if (await confirmNoteDeletion()) {
        deleteNote.mutate({ number: item.number, version: item.version });
      }
    },
    isOpeningChat: isOpening,
    openSourceChat: () => {
      if (item.source_chat_id) {
        void openChat(item.source_chat_id);
      }
    },
  };
}
